<template>
    <div class="review-creative-carousel modal-content" :class="{ 'modal-content-bundle': isBundle }">
        <div class="relative">
            <template v-if="currentCreative">
                <!-- Header -->
                <div class="modal-header">
                    <h5 class="modal-title">Review Creative</h5>
                    <div class="modal-buttons">
                        <download-order-items download-type="creative" :query="{ filter: { id: creativeIds } }" />
                        <share-order-items share-type="creative" :query="{ filter: { id: creativeIds } }" />
                        <button type="button" class="btn-close" :class="{ 'text-white opacity-100': isBundle, 'text-black': !isBundle }" @click="close">
                            <span class="fal fa-fw fa-times"></span>
                            <span class="sr-only">Close Modal</span>
                        </button>
                    </div>
                </div>

                <!-- Item Name -->
                <div class="px-3 pt-1 text-lg roboto">
                    {{ itemName }}
                    <small v-if="isBundle">in {{ parentName }}</small>
                </div>

                <!-- Carousel -->
                <div class="jgile-carousel">
                    <div class="modal-body">
                        <!-- Counter -->
                        <div class="counter">
                            {{ currentPage }} of {{ total }} Total Creative<br />
                            <run-dates :start="startDate" :end="endDate" />
                        </div>

                        <!-- Carousel Navigation -->
                        <div class="row switcher">
                            <!-- Previous Button -->
                            <div v-if="total > 1" class="col-2">
                                <button class="btn prev" @click="previous" :disabled="loading">
                                    <i class="fal fa-chevron-left fa-fw"></i>
                                    <span class="sr-only">Previous Creative</span>
                                </button>
                            </div>

                            <!-- Creative Display -->
                            <div :class="total > 1 ? 'col-8' : 'col-12'">
                                <div class="current-item">
                                    <div class="current-item-content">
                                        <div v-if="loading" class="text-center py-4">
                                            <i class="fa fa-spinner fa-spin"></i>
                                        </div>
                                        <template v-else>
                                            <creative-display :creative="currentCreative" />

                                            <!-- Creative Requirements -->
                                            <div class="text-left mt-3">
                                                <label>Creative Requirements</label>
                                                <div v-if="artworkUrl" class="d-block">
                                                    <a class="btn btn-link btn-sm mb-2" :href="artworkUrl" target="_blank"> Download Creative Document </a>
                                                </div>
                                                <div v-if="artworkText" class="shadow-sm scroll-text text-left w-100">
                                                    {{ artworkText }}
                                                </div>
                                            </div>

                                            <!-- Media Metadata -->
                                            <p v-if="currentCreative.media" class="mt-2">
                                                <span v-if="currentCreative.media.name" class="badge badge-light"> Name: {{ currentCreative.media.name }} </span>
                                                <span v-if="currentCreative.media.human_readable_size" class="badge badge-light">
                                                    Size: {{ currentCreative.media.human_readable_size }}
                                                </span>
                                                <template v-if="currentCreative.creative_type === 'image'">
                                                    <span v-if="currentCreative.media.dimensions" class="badge badge-light">
                                                        Dimensions: {{ currentCreative.media.dimensions }}
                                                    </span>
                                                    <br />
                                                    <span class="badge badge-light">
                                                        Required Dimensions: {{ currentCreative.spec_width }}x{{ currentCreative.spec_height }}px
                                                    </span>
                                                </template>
                                            </p>
                                        </template>
                                    </div>
                                </div>
                            </div>

                            <!-- Next Button -->
                            <div v-if="total > 1" class="col-2">
                                <button class="btn next" @click="next" :disabled="loading">
                                    <i class="fal fa-chevron-right fa-fw"></i>
                                    <span class="sr-only">Next Creative</span>
                                </button>
                            </div>
                        </div>

                        <!-- Sub-switcher: Change Request Button -->
                        <div class="row sub-switcher">
                            <div class="col">
                                <!-- Status: None - Show Change Request Form -->
                                <template v-if="currentStatus === 'none'">
                                    <button class="btn btn-primary btn-sm" @click="showChangeRequestForm = !showChangeRequestForm">Request Creative Change</button>
                                    <div v-if="showChangeRequestForm" class="change-request-popup mt-2">
                                        <change-request-form :creative="currentCreative" @submitted="onChangeRequestSubmitted" @cancel="showChangeRequestForm = false" />
                                    </div>
                                </template>

                                <!-- Status: Change Requested - Show Remove Button -->
                                <button v-else-if="currentStatus === 'change_requested'" id="removeChangeRequestButton" class="btn btn-primary btn-sm" @click="setStatus('none')">
                                    Change Requested
                                </button>

                                <!-- Other Status - Disabled -->
                                <button v-else class="btn btn-primary btn-sm" disabled>Request Creative Change</button>
                            </div>
                        </div>
                    </div>

                    <!-- Footer: Action Buttons -->
                    <div class="modal-footer d-flex justify-content-around">
                        <!-- Status: None -->
                        <template v-if="currentStatus === 'none'">
                            <button class="btn btn-primary text-sm fixed-width" @click="showDenyForm = !showDenyForm">Deny</button>
                            <div v-if="showDenyForm" class="deny-popup">
                                <deny-creative-form :creative="currentCreative" :creative-ids="creativeIds" @submitted="onDenySubmitted" @cancel="showDenyForm = false" />
                            </div>

                            <!-- Approve Button(s) -->
                            <div v-if="total > 1" class="btn-group fixed-width">
                                <button class="btn btn-secondary" @click="approve">Approve</button>
                                <button class="btn btn-secondary approve-and-next" @click="approveAndNext">
                                    <span class="sr-only">Approve and Next</span>
                                    <i class="fal fa-chevron-right fa-fw"></i>
                                </button>
                            </div>
                            <button v-else class="btn btn-secondary fixed-width" @click="approve">Approve</button>
                        </template>

                        <!-- Status: Approved -->
                        <template v-if="currentStatus === 'approved'">
                            <button class="btn btn-primary text-sm fixed-width" disabled>Deny</button>
                            <button class="btn btn-success fixed-width" @click="setStatus('none')">Approved</button>
                        </template>

                        <!-- Status: Denied -->
                        <template v-if="currentStatus === 'denied'">
                            <button class="btn btn-danger fixed-width" @click="setStatus('none')">Denied</button>
                            <button class="btn btn-secondary" disabled>Approve</button>
                        </template>

                        <!-- Status: Change Requested -->
                        <template v-if="currentStatus === 'change_requested'">
                            <button class="btn btn-danger" disabled>Denied</button>
                            <button class="btn btn-secondary" disabled>Approve</button>
                        </template>

                        <!-- Done Button -->
                        <button v-if="isDone" class="btn btn-primary fixed-width" @click="close">Done</button>

                        <!-- Deny Order Button (when isOrder mode) -->
                        <div v-if="isOrder" class="d-flex w-100">
                            <div class="mr-auto">
                                <button class="btn btn-plain text-danger fixed-width" :disabled="disabled" @click="showDenyOrderForm = !showDenyOrderForm">Deny Order</button>
                                <div v-if="showDenyOrderForm" class="deny-order-popup">
                                    <deny-order-form :order-id="orderId" @submitted="onDenyOrderSubmitted" @cancel="showDenyOrderForm = false" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import CreativeTableApi from '@js/services/CreativeTableApi';

export default {
    name: 'ReviewCreativeCarousel',

    props: {
        /**
         * Array of creative IDs to review.
         */
        creativeIds: {
            type: Array,
            required: true,
        },

        /**
         * Order ID for context.
         */
        orderId: {
            type: [Number, String],
            required: true,
        },

        /**
         * Whether the carousel is disabled.
         */
        disabled: {
            type: Boolean,
            default: false,
        },

        /**
         * Whether this is an order-level review (show Deny Order button).
         */
        isOrder: {
            type: Boolean,
            default: false,
        },

        /**
         * Initial page to display.
         */
        initialPage: {
            type: Number,
            default: 1,
        },
    },

    data() {
        return {
            creativeItems: [],
            currentPage: this.initialPage,
            loading: false,
            showChangeRequestForm: false,
            showDenyForm: false,
            showDenyOrderForm: false,
            orderData: null,
        };
    },

    computed: {
        /**
         * Total number of creative items.
         */
        total() {
            return this.creativeItems.length;
        },

        /**
         * Current creative being displayed.
         */
        currentCreative() {
            if (this.total === 0) return null;
            const index = this.currentPage - 1;
            return this.creativeItems[index] || null;
        },

        /**
         * Current creative status (normalized by the API for the current user).
         * Uses the normalized_status from the API which is context-aware.
         */
        currentStatus() {
            if (!this.currentCreative) return 'none';
            return this.currentCreative.normalized_status || 'none';
        },

        /**
         * Whether current creative is part of a bundle.
         */
        isBundle() {
            if (!this.currentCreative || !this.currentCreative.model) return false;
            return this.currentCreative.model.parent_id != null;
        },

        /**
         * Item name to display.
         */
        itemName() {
            if (!this.currentCreative) return '';
            if (this.currentCreative.model && this.currentCreative.model.name) {
                return this.currentCreative.model.name;
            }
            return this.currentCreative.name || '';
        },

        /**
         * Parent item name for bundles.
         */
        parentName() {
            if (!this.currentCreative || !this.currentCreative.parent_order_item) return '';
            return this.currentCreative.parent_order_item.name || '';
        },

        /**
         * Start date for run dates display.
         */
        startDate() {
            if (this.isOrder && this.orderData) {
                return this.orderData.start_date;
            }
            if (!this.currentCreative) return null;
            if (this.currentCreative.start_date) return this.currentCreative.start_date;
            if (this.currentCreative.model && this.currentCreative.model.start_date) {
                return this.currentCreative.model.start_date;
            }
            return null;
        },

        /**
         * End date for run dates display.
         */
        endDate() {
            if (this.isOrder && this.orderData) {
                return this.orderData.end_date;
            }
            if (!this.currentCreative) return null;
            if (this.currentCreative.end_date) return this.currentCreative.end_date;
            if (this.currentCreative.model && this.currentCreative.model.end_date) {
                return this.currentCreative.model.end_date;
            }
            return null;
        },

        /**
         * Artwork document URL.
         */
        artworkUrl() {
            if (!this.currentCreative || !this.currentCreative.asset) return null;
            return this.currentCreative.asset.artwork_url || null;
        },

        /**
         * Artwork text/requirements.
         */
        artworkText() {
            if (!this.currentCreative || !this.currentCreative.asset) return null;
            return this.currentCreative.asset.artwork_text || null;
        },

        /**
         * Whether all creative has been reviewed.
         */
        isDone() {
            return this.creativeItems.every(c => c.normalized_status !== 'none');
        },
    },

    watch: {
        creativeIds: {
            handler() {
                this.loadCreatives();
            },
            immediate: true,
        },
    },

    created() {
        Bus.$on('refreshReviewCreative', this.loadCreatives);
    },

    beforeDestroy() {
        Bus.$off('refreshReviewCreative', this.loadCreatives);
    },

    methods: {
        /**
         * Load creative items from API.
         */
        async loadCreatives() {
            if (this.creativeIds.length === 0) return;

            this.loading = true;
            try {
                const response = await CreativeTableApi.getByIds(this.creativeIds);
                this.creativeItems = response.data.data || response.data;

                // Reset page if out of bounds
                if (this.currentPage > this.total) {
                    this.currentPage = 1;
                }
            } catch (error) {
                Bus.$emit('toast', 'Failed to load creative items.', 'error');
            } finally {
                this.loading = false;
            }
        },

        /**
         * Go to previous creative.
         */
        previous() {
            this.hideAllForms();
            if (this.currentPage === 1) {
                this.currentPage = this.total;
            } else {
                this.currentPage--;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Go to next creative.
         */
        next() {
            this.hideAllForms();
            if (this.currentPage === this.total) {
                this.currentPage = 1;
            } else {
                this.currentPage++;
            }
            this.$emit('page-change', this.currentPage);
        },

        /**
         * Hide all popup forms.
         */
        hideAllForms() {
            this.showChangeRequestForm = false;
            this.showDenyForm = false;
            this.showDenyOrderForm = false;
        },

        /**
         * Set status for current creative.
         */
        async setStatus(status) {
            try {
                await CreativeTableApi.updateStatus(this.currentCreative.id, {
                    status,
                    creative_ids: this.creativeIds,
                });
                await this.loadCreatives();
                Bus.$emit('refreshOrdersTable');
            } catch (error) {
                Bus.$emit('toast', 'Failed to update status.', 'error');
            }
        },

        /**
         * Approve current creative.
         */
        async approve() {
            await this.setStatus('approved');
        },

        /**
         * Approve current creative and go to next.
         */
        async approveAndNext() {
            await this.setStatus('approved');
            this.next();
        },

        /**
         * Handle change request form submission.
         */
        onChangeRequestSubmitted() {
            this.showChangeRequestForm = false;
            this.loadCreatives();
        },

        /**
         * Handle deny form submission.
         */
        onDenySubmitted() {
            this.showDenyForm = false;
            this.loadCreatives();
        },

        /**
         * Handle deny order form submission.
         */
        onDenyOrderSubmitted() {
            this.showDenyOrderForm = false;
            this.close();
        },

        /**
         * Close the carousel modal.
         */
        close() {
            this.$emit('close');
            Bus.$emit('closeReviewCreative');
        },
    },
};
</script>

<style scoped>
.review-creative-carousel {
    width: 100%;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #dee2e6;
}

.modal-title {
    margin: 0;
    font-size: 1.25rem;
}

.modal-buttons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-close {
    background: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.25rem;
}

.modal-body {
    padding: 1rem;
}

.counter {
    text-align: center;
    margin-bottom: 1rem;
    font-size: 0.9rem;
    color: #666;
}

.switcher {
    align-items: center;
    min-height: 200px;
}

.current-item {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 180px;
}

.current-item-content {
    width: 100%;
    text-align: center;
}

.btn.prev,
.btn.next {
    padding: 10px 15px;
    background: transparent;
    border: 1px solid #ddd;
    border-radius: 4px;
    cursor: pointer;
}

.btn.prev:hover:not(:disabled),
.btn.next:hover:not(:disabled) {
    background-color: #f5f5f5;
}

.sub-switcher {
    margin-top: 1rem;
    text-align: center;
}

.modal-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    border-top: 1px solid #dee2e6;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.fixed-width {
    min-width: 80px;
}

.btn-group {
    display: inline-flex;
}

.btn-group .btn:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.btn-group .btn:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -1px;
}

.approve-and-next {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
}

.change-request-popup,
.deny-popup,
.deny-order-popup {
    position: absolute;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 100;
    width: 300px;
    padding: 10px;
}

.scroll-text {
    max-height: 150px;
    overflow-y: auto;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 4px;
}

.badge-light {
    background-color: #f8f9fa;
    color: #333;
    padding: 0.25rem 0.5rem;
    margin-right: 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
}

.modal-content-bundle .modal-header {
    background-color: #343a40;
    color: white;
}
</style>
